/**
 * ProjectDataTools
 * Seed demo data + delete project — extracted from ProjectSettingsPage.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { projectsApi } from '../../api/projects';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';

export function ProjectDataTools() {
  const navigate = useNavigate();
  const { project, refreshProjects } = useProject();

  const [isSeeding, setIsSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleSeedDemo = useCallback(async () => {
    if (!project) return;
    setIsSeeding(true);
    setSeedResult(null);
    setSeedError(null);

    try {
      const boardsRes = await fetch(`/api/projects/${project.id}/work/boards`);
      const boardsData = await boardsRes.json();
      const boardId = boardsData.boards?.[0]?.id;
      if (!boardId) {
        setSeedError('No board found. Create a board first.');
        setIsSeeding(false);
        return;
      }

      const res = await fetch('/api/orchestration/admin/seed-demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: project.id, boardId }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(err.message || 'Seed failed');
      }

      const data = await res.json();
      setSeedResult(
        `Created ${data.epicsCreated} epics, ${data.storiesCreated} stories, ${data.docsCreated} docs, ${data.requirementsSeeded} requirements. ` +
        `Readiness: ${data.assessments?.map((a: any) => `${a.epicId}: ${a.score} (${a.status})`).join(', ')}`
      );
    } catch (err) {
      console.error('Seed failed:', err);
      setSeedError(err instanceof Error ? err.message : 'Failed to seed demo data');
    } finally {
      setIsSeeding(false);
    }
  }, [project]);

  const handleDelete = useCallback(async () => {
    if (!project) return;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await projectsApi.delete(project.id);
      await refreshProjects();
      navigate('/projects');
    } catch (err) {
      console.error('Failed to delete project:', err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete project');
      setIsDeleting(false);
    }
  }, [project, refreshProjects, navigate]);

  if (!project) {
    return (
      <Panel>
        <PanelBody>
          <div className="text-center py-8 text-muted text-sm">
            Select a project to access data tools.
          </div>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <div className="space-y-6">
      {/* Demo Data */}
      <Panel>
        <PanelHeader title="Demo Data" />
        <PanelBody>
          <div className="space-y-4">
            {seedResult && (
              <div className="p-2.5 bg-green-50 border border-green-200 rounded-lg text-green-800 text-[12px]">
                {seedResult}
              </div>
            )}
            {seedError && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-800 text-[12px]">
                {seedError}
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-[13px] font-medium text-text">Seed demo data</h3>
                <p className="text-[12px] text-muted mt-0.5">
                  Creates epics, stories, specs, test plans, and requirement tracking. Idempotent.
                </p>
              </div>
              <button
                onClick={handleSeedDemo}
                disabled={isSeeding}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[12px] font-medium flex items-center gap-1.5"
              >
                {isSeeding ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                    Seeding...
                  </>
                ) : (
                  'Create Demo Data'
                )}
              </button>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Danger Zone */}
      <Panel className="border-red-200">
        <PanelHeader title="Danger Zone" accent="red" />
        <PanelBody>
          <div className="space-y-4">
            {deleteError && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-800 text-[12px]">
                {deleteError}
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-[13px] font-medium text-text">Delete this project</h3>
                <p className="text-[12px] text-muted mt-0.5">
                  Permanently removes this project and all associated data.
                </p>
              </div>
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-[12px] font-medium"
                >
                  Delete Project
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                    className="px-3 py-1.5 border border-line-strong text-text rounded-lg hover:bg-surface-muted transition-colors text-[12px] font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[12px] font-medium flex items-center gap-1.5"
                  >
                    {isDeleting ? (
                      <>
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                        Deleting...
                      </>
                    ) : (
                      'Confirm Delete'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}
